import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Users, 
  CheckCircle, 
  Clock, 
  TrendingUp, 
  LogOut,
  RefreshCw,
  BarChart3,
  Calendar
} from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import UsageMonitoring from "@/components/admin/UsageMonitoring";
import ClickAnalytics from "@/components/admin/ClickAnalytics";

interface WizardSession {
  id: string;
  session_id: string;
  started_at: string;
  completed_at: string | null;
  current_step: string;
  age_range: string | null;
  family_status: string | null;
  financial_situation: string | null;
  main_reason: string | null;
  property_type: string | null;
  property_value: string | null;
  location: string | null;
  payment_form: string | null;
  priority: string | null;
  recommendations: unknown;
}

interface Stats {
  totalSessions: number;
  completedSessions: number;
  completionRate: number;
  todaySessions: number;
}

const AdminDashboard = () => {
  const [sessions, setSessions] = useState<WizardSession[]>([]);
  const [allSessions, setAllSessions] = useState<WizardSession[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      navigate("/admin");
      return;
    }

    // Verify admin role
    const { data: roleData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin")
      .maybeSingle();

    if (!roleData) {
      await supabase.auth.signOut();
      navigate("/admin");
      return;
    }

    setIsAuthenticated(true);
    fetchData();
  };

  const fetchData = async () => {
    setIsLoading(true);
    
    try {
      // Fetch recent sessions for table display
      const { data: sessionsData, error: sessionsError } = await supabase
        .from("wizard_sessions")
        .select("*")
        .order("started_at", { ascending: false })
        .limit(100);

      if (sessionsError) throw sessionsError;

      setSessions(sessionsData || []);

      // Fetch ALL sessions (lightweight) for monitoring charts
      const { data: allData } = await supabase
        .from("wizard_sessions")
        .select("id, session_id, started_at, completed_at, current_step")
        .order("started_at", { ascending: false })
        .limit(5000);

      const monitoringSessions = (allData || []) as WizardSession[];
      setAllSessions(monitoringSessions);

      // Calculate stats from full dataset
      const total = monitoringSessions.length;
      const completed = monitoringSessions.filter(s => s.completed_at !== null).length;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayCount = monitoringSessions.filter(s => new Date(s.started_at) >= today).length;

      setStats({
        totalSessions: total,
        completedSessions: completed,
        completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
        todaySessions: todayCount,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin");
  };

  const getStepBadge = (step: string, completed: boolean) => {
    if (completed) {
      return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Abgeschlossen</Badge>;
    }
    
    const stepLabels: Record<string, string> = {
      intro: "Start",
      personal: "Persönlich",
      property: "Immobilie",
      preferences: "Wünsche",
      results: "Ergebnisse",
    };
    
    return <Badge variant="secondary">{stepLabels[step] || step}</Badge>;
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Admin Dashboard | eigenheimverrentung.de</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b bg-card">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-primary">Admin Dashboard</h1>
              <p className="text-sm text-muted-foreground">Digitaler Berater Analytics</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchData} disabled={isLoading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Aktualisieren
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Abmelden
              </Button>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Gesamt Sessions
                </CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <div className="text-2xl font-bold">{stats?.totalSessions || 0}</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Abgeschlossen
                </CardTitle>
                <CheckCircle className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <div className="text-2xl font-bold text-green-600">{stats?.completedSessions || 0}</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Abschlussrate
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <div className="text-2xl font-bold">{stats?.completionRate || 0}%</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Heute
                </CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <div className="text-2xl font-bold">{stats?.todaySessions || 0}</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Usage Monitoring */}
          {!isLoading && (
            <div className="mb-8">
              <UsageMonitoring sessions={allSessions} />
            </div>
          )}

          {/* Click Analytics */}
          <div className="mb-8">
            <ClickAnalytics />
          </div>

          {/* Sessions Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Beratungssessions
              </CardTitle>
              <CardDescription>
                Die letzten 100 Sessions mit allen Eingaben
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Noch keine Sessions vorhanden</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Datum</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Alter</TableHead>
                        <TableHead>Familienstand</TableHead>
                        <TableHead>Immobilientyp</TableHead>
                        <TableHead>Wert</TableHead>
                        <TableHead>Lage</TableHead>
                        <TableHead>Priorität</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessions.map((session) => (
                        <TableRow key={session.id}>
                          <TableCell className="whitespace-nowrap">
                            {format(new Date(session.started_at), "dd.MM.yy HH:mm", { locale: de })}
                          </TableCell>
                          <TableCell>
                            {getStepBadge(session.current_step, !!session.completed_at)}
                          </TableCell>
                          <TableCell>{session.age_range || "-"}</TableCell>
                          <TableCell>{session.family_status || "-"}</TableCell>
                          <TableCell>{session.property_type || "-"}</TableCell>
                          <TableCell>{session.property_value || "-"}</TableCell>
                          <TableCell>{session.location || "-"}</TableCell>
                          <TableCell>{session.priority || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    </>
  );
};

export default AdminDashboard;
